import type { AppBridge } from './bridge.ts';

export type ApiFetch = (input: string, init?: RequestInit) => Promise<Response>;

function resolveUrl(baseUrl: string, input: string): string {
  if (/^https?:\/\//i.test(input) || input.startsWith('/')) return input;
  return `${baseUrl.replace(/\/+$/, '')}/${input}`;
}

function withToken(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers, credentials: 'omit' };
}

/**
 * A fetch that sends the session token to the app's own backend. On a 401 it asks the dashboard
 * for a fresh token once and retries; a second 401 is returned to the caller.
 *
 * Relative inputs (`notes`, `api/notes`) are resolved against `baseUrl`. Request bodies must be
 * re-readable (string, FormData, Blob, URLSearchParams) because of the retry.
 */
export function createApiFetch(
  bridge: Pick<AppBridge, 'getFreshToken' | 'refreshToken'>,
  baseUrl = '',
  doFetch?: typeof fetch,
): ApiFetch {
  // Looked up per call so tests (and polyfills) that replace globalThis.fetch later still apply.
  const send = (url: string, init: RequestInit) => (doFetch ?? globalThis.fetch)(url, init);
  return async (input, init) => {
    const url = resolveUrl(baseUrl, input);
    const token = await bridge.getFreshToken();
    const response = await send(url, withToken(init, token));
    if (response.status !== 401) return response;

    const fresh = await bridge.refreshToken();
    return send(url, withToken(init, fresh));
  };
}
